// src/components/NewsletterSection.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaRegNewspaper, FaShieldAlt } from 'react-icons/fa';
import { subscribeToNewsletter } from '../../../services/newsletterService';
import { toast } from 'react-toastify';
import TechLoader from '../../common/TechLoader';

const NewsletterSection = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      await subscribeToNewsletter(email);
      toast.success('Successfully subscribed to newsletter!');
      setEmail('');
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to subscribe. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section 
      className={`relative isolate overflow-hidden py-16 sm:py-24 lg:py-32 ${
        isDark 
          ? 'bg-gradient-to-r from-gray-900 to-black'
          : 'bg-gradient-to-r from-gray-100 to-white'
      }`}
    >
      <motion.div 
        className="mx-auto max-w-7xl px-6 lg:px-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
      >
        <motion.div 
          className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-2"
          variants={containerVariants}
        >
          <motion.div 
            className="max-w-xl lg:max-w-lg"
            variants={itemVariants}
          >
            <h2 className={`text-4xl font-semibold tracking-tight mb-6 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Subscribe to our newsletter
            </h2>
            <p className={`mt-4 text-lg ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Stay updated with the latest articles, insights, and resources. We promise no spam, just valuable
              content to help you stay ahead in tech.
            </p>
            <motion.form 
              onSubmit={handleSubmit} 
              className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md"
              variants={itemVariants}
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className={`min-w-0 flex-auto rounded-md border-0 px-3.5 py-2 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6 ${
                  isDark 
                    ? 'bg-white/5 text-white ring-white/10'
                    : 'bg-white text-gray-900 ring-gray-300'
                }`}
                required
              />
              <motion.button
                type="submit"
                disabled={isLoading}
                className={`flex-none rounded-md px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDark 
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-blue-500 hover:bg-blue-600'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isLoading ? 'Subscribing...' : 'Subscribe'}
              </motion.button>
            </motion.form>
          </motion.div>

          <motion.dl 
            className="grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2 lg:pt-2"
            variants={containerVariants}
          >
            <motion.div 
              className="flex flex-col items-start"
              variants={itemVariants}
            >
              <div className={`rounded-md p-2 ${
                isDark ? 'bg-blue-900/30' : 'bg-blue-100'
              }`}>
                <FaRegNewspaper className={`h-6 w-6 ${
                  isDark ? 'text-blue-400' : 'text-blue-600'
                }`} />
              </div>
              <dt className={`mt-4 font-semibold ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                Weekly articles
              </dt>
              <dd className={`mt-2 leading-7 ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Get exclusive insights and updates on the latest in technology, curated by our expert team.
              </dd>
            </motion.div>

            <motion.div 
              className="flex flex-col items-start"
              variants={itemVariants}
            >
              <div className={`rounded-md p-2 ${
                isDark ? 'bg-blue-900/30' : 'bg-blue-100'
              }`}>
                <FaShieldAlt className={`h-6 w-6 ${
                  isDark ? 'text-blue-400' : 'text-blue-600'
                }`} />
              </div>
              <dt className={`mt-4 font-semibold ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                No spam
              </dt>
              <dd className={`mt-2 leading-7 ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Your inbox is safe with us. We only send valuable content to help you stay informed.
              </dd>
            </motion.div>
          </motion.dl>
        </motion.div>
      </motion.div>

      <div className="absolute left-1/2 top-0 -z-10 -translate-x-1/2 blur-3xl xl:-top-6" aria-hidden="true">
        <div
          className="aspect-[1155/678] w-[72.1875rem] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30"
          style={{
            clipPath:
              'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
          }}
        />
      </div>
    </section>
  );
};

export default NewsletterSection;
